import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { whatsappBusinessAccountsApi, whatsappNumbersApi } from '@/lib/api/whatsapp-numbers.api';

import type {
  CreateWhatsAppBusinessAccountDto,
  UpdateWhatsAppBusinessAccountDto,
  WhatsAppAssignInboundNumberDto,
} from '@repo/shared-types';

const WHATSAPP_NUMBERS_QUERY_KEY = ['whatsapp-numbers'];
const WHATSAPP_BUSINESS_ACCOUNTS_QUERY_KEY = ['whatsapp-business-accounts'];

export function useWhatsAppNumbers() {
  return useQuery({
    queryKey: WHATSAPP_NUMBERS_QUERY_KEY,
    queryFn: () => whatsappNumbersApi.list(),
  });
}

export function useSyncWhatsAppNumbers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => whatsappNumbersApi.sync(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WHATSAPP_NUMBERS_QUERY_KEY });
    },
  });
}

export function useAssignInboundWhatsAppNumber() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      numberId,
      payload,
    }: {
      numberId: string;
      payload: WhatsAppAssignInboundNumberDto;
    }) => whatsappNumbersApi.assignInbound(numberId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WHATSAPP_NUMBERS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['configurations'] });
    },
  });
}

// ── Business accounts (WABAs) ─────────────────────────────────────

export function useWhatsAppBusinessAccounts() {
  return useQuery({
    queryKey: WHATSAPP_BUSINESS_ACCOUNTS_QUERY_KEY,
    queryFn: () => whatsappBusinessAccountsApi.list(),
  });
}

export function useCreateWhatsAppBusinessAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateWhatsAppBusinessAccountDto) =>
      whatsappBusinessAccountsApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WHATSAPP_BUSINESS_ACCOUNTS_QUERY_KEY });
    },
  });
}

export function useUpdateWhatsAppBusinessAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateWhatsAppBusinessAccountDto }) =>
      whatsappBusinessAccountsApi.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WHATSAPP_BUSINESS_ACCOUNTS_QUERY_KEY });
    },
  });
}

export function useSubscribeWhatsAppBusinessAccount() {
  return useMutation({
    mutationFn: (id: string) => whatsappBusinessAccountsApi.subscribe(id),
  });
}

export function useDeleteWhatsAppBusinessAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => whatsappBusinessAccountsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WHATSAPP_BUSINESS_ACCOUNTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: WHATSAPP_NUMBERS_QUERY_KEY });
    },
  });
}
